import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Markdown from 'markdown-to-jsx';
import pluralize from 'pluralize';

import { useAppSettingsQuery } from '@queries/AppQueries';
import {
  Entertainer,
  StandoutScreen,
  StandoutScreenSection,
  useEntertainersStandoutsQuery,
  useHydratedEntertainersQuery,
} from '@queries/EntertainerQueries';

import {
  useAnswerRecommendationQuestionMutation,
  useRecommendEntertainerMutation,
} from '@mutations/CritiqueMutations';
import { useSendBulkEntertainerBadges } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCityById, useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { useNavigate, useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { SearchField } from '@components/form/SearchField/SearchField';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { LazyLoad } from '@components/layout/LazyLoad';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

import { useEntertainerImpression } from '../EntertainerDetailsScreen/sendEntertainerDetailsPageView';

const EntertainerSupportCard: FC<{
  entertainer: Entertainer;
  onClick: (entertainer: Entertainer) => void;
}> = ({ entertainer, onClick }) => {
  const divRef = useRef(null);

  useEntertainerImpression(entertainer._id, divRef);

  const image = entertainer.avatar || entertainer.featuredMedia?.[0]?.image;
  const imageSize = 60;

  return (
    <Card style={{ borderRadius: 8 }} onClick={() => onClick(entertainer)}>
      <div ref={divRef}>
        <Card.Header
          className="p-1"
          avatar={
            <div
              style={{
                position: 'relative',
                width: imageSize,
                height: imageSize,
              }}
            >
              <div style={{ borderRadius: 8, overflow: 'hidden', width: '100%', height: '100%' }}>
                {image ? (
                  <AsyncImage
                    src={image}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    placeholder={
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'rgba(255,255,255,.2)',
                        }}
                      />
                    }
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255,255,255,.2)',
                    }}
                  />
                )}
              </div>
            </div>
          }
          tagline={entertainer.cities.map((city) => city.name).join(' • ')}
          title={entertainer.title}
          subtitle={[
            `${entertainer.badgeCount} ${pluralize('Badge', entertainer.badgeCount)}`,
            `${entertainer.featuredMedia.length} ${pluralize('Look', entertainer.featuredMedia.length)}`,
            `${entertainer.details.categories.length} ${pluralize('Category', entertainer.details.categories.length)}`,
          ].join(' • ')}
          action={
            <div className="fs-6 px-2">
              <FontAwesomeIcon name="chevron-right" />
            </div>
          }
        />
      </div>
    </Card>
  );
};

const RecommendationEmptyState: FC<{
  cityId: string;
  searchValue: string;
  recommendationSlug: string;
  onResetSearchValue: () => void;
}> = ({ cityId, searchValue, recommendationSlug, onResetSearchValue }) => {
  const { answerRecommendationQuestionAsync } = useAnswerRecommendationQuestionMutation();
  const [entertainerName, setEntertainerName] = useState('');
  const [didSubmit, setDidSubmit] = useState(false);
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();

  const handleAddEntertainer = () => {
    if (entertainerName) {
      track('Recommend Custom', {
        recommendationSlug,
        entertainerName,
      });

      createAuthGateHandler(
        async () => {
          await answerRecommendationQuestionAsync({
            entertainerIds: [],
            questionSlug: recommendationSlug,
            custom: entertainerName,
          });

          setDidSubmit(true);
        },
        {
          cityId,
          entertainerCallback: () => {},
        },
      );
    }
  };

  const buttonText = useMemo(() => {
    if (didSubmit) {
      return `Recommended!`;
    }

    if (entertainerName) {
      return `Recommend "${entertainerName}"`;
    }

    return 'Recommend';
  }, [didSubmit, entertainerName]);

  return (
    <EmptyState
      title="Did we miss someone?"
      content={
        <>
          <p>{`There are no entertainers matching "${searchValue}". Use the input below to add them.`}</p>
          <div className="mb-2">
            <Input
              name="entertainerName"
              disabled={didSubmit}
              placeholder="Entertainer Name"
              onChange={(e) => setEntertainerName(e.target.value)}
            />
          </div>
          <Button
            disabled={!entertainerName || didSubmit}
            roundness="rounded"
            onClick={handleAddEntertainer}
          >
            {buttonText}
          </Button>
          {didSubmit && (
            <Button
              className="mt-2"
              roundness="rounded"
              color="neutral"
              onClick={onResetSearchValue}
            >
              Return to list
            </Button>
          )}
        </>
      }
    />
  );
};

const RecommendEntertainersPopupContent: FC<{
  entertainerStandouts: StandoutScreen;
  entertainers: Record<string, Entertainer>;
  badgeId?: string;
  cityId: string;
  recommendationSlug?: string;
  onClose: () => void;
}> = ({ entertainerStandouts, entertainers, badgeId, cityId, recommendationSlug, onClose }) => {
  const [selectionRecord, setSelectionRecord] = useState<Record<string, boolean>>({});
  const titleDivRef = useRef<HTMLDivElement>(null);
  const [contentTopPadding, setContentTopPadding] = useState(0);
  const { answerRecommendationQuestionAsync } = useAnswerRecommendationQuestionMutation();
  const { sendBulkEntertainerBadgesAsync } = useSendBulkEntertainerBadges();
  const [searchValue, setSearchValue] = useState('');
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();

  useLayoutEffect(() => {
    setTimeout(() => {
      const rect = titleDivRef.current?.getBoundingClientRect();
      if (rect?.height) {
        setContentTopPadding(rect.height - 16);
      }
    }, 250);
  }, []);

  const canSubmit = Object.values(selectionRecord).some((v) => v);

  const handleSelectAction = (key: string) => {
    setSelectionRecord((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = () => {
    const entertainerIds = Object.entries(selectionRecord)
      .filter(([, value]) => value)
      .map(([entertainerId]) => entertainerId);

    if (recommendationSlug) {
      track('Recommend Entertainers', {
        recommendationSlug,
        entertainerIds,
      });
    } else {
      track('Recommend Badge Entertainers', {
        badgeId,
        entertainerIds,
      });
    }

    createAuthGateHandler(
      () => {
        if (recommendationSlug) {
          answerRecommendationQuestionAsync({ entertainerIds, questionSlug: recommendationSlug });
        } else {
          const badges = badgeId
            ? entertainerIds.map((entertainerId) => ({
                entertainerId,
                badgeId,
                reason: '', // TODO: We should have something here
              }))
            : [];

          if (badges.length) {
            sendBulkEntertainerBadgesAsync({ badges });
          }
        }
        onClose();
      },
      {
        cityId,
        entertainerCallback: () => {},
      },
    );
  };

  const sanitizedSearchValue = searchValue.trim().toLowerCase();

  const filteredEntertainers = useMemo(() => {
    const entertainerValues = Object.values(entertainers);

    if (!sanitizedSearchValue) {
      return entertainerValues;
    }

    return entertainerValues.filter((entertainer) =>
      entertainer.title.toLowerCase().includes(sanitizedSearchValue),
    );
  }, [entertainers, sanitizedSearchValue]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={titleDivRef}
        style={{
          position: 'fixed',
          background: '#111',
          zIndex: 10,
          width: '100%',
          top: 0,
          left: 0,
          padding: '1.5rem 16px 0',
        }}
      >
        <TitleToolbar
          text={
            <div>
              <div
                style={{
                  textTransform: 'uppercase',
                  fontSize: '.7rem',
                  letterSpacing: '.1rem',
                  marginBottom: -4,
                  opacity: 0.7,
                }}
              >
                Recommend
              </div>
              <div>{entertainerStandouts.title}</div>
            </div>
          }
          size="md"
          action={
            <Button
              disabled={!canSubmit}
              roundness="rounded"
              size="sm"
              className="py-2 px-3 recommend-entertainer-popup-submit-button"
              onClick={handleSubmit}
            >
              Recommend
            </Button>
          }
        />
      </div>

      <div className="d-grid gap-2" style={{ paddingTop: contentTopPadding }}>
        <Input
          name="recommendEntertainersPopupSearch"
          placeholder="Search entertainers..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
        {filteredEntertainers.map(({ _id, title, avatar, featuredMedia }) => (
          <div
            key={_id}
            className="d-flex gap-2 align-items-center py-1 px-2"
            style={{ backgroundColor: 'rgba(255,255,255,.1)', borderRadius: 8 }}
            onClick={() => handleSelectAction(_id)}
          >
            <div style={{ fontSize: '1.1rem' }}>
              {selectionRecord[_id] ? (
                <span style={{ color: 'var(--color-green)' }}>
                  <FontAwesomeIcon name="check-circle" />
                </span>
              ) : (
                <FontAwesomeIcon faStyle="far" name="circle" />
              )}
            </div>
            <img
              src={avatar ?? featuredMedia?.[0]?.image}
              alt={title}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                ...(selectionRecord[_id] ? {} : { filter: 'grayscale(1)', opacity: 0.5 }),
              }}
            />
            <strong className="recommend-entertainer-item-label">{title}</strong>
          </div>
        ))}
        {filteredEntertainers.length === 0 && sanitizedSearchValue && (
          <RecommendationEmptyState
            cityId={cityId}
            searchValue={searchValue}
            recommendationSlug={recommendationSlug}
            onResetSearchValue={() => setSearchValue('')}
          />
        )}
      </div>
    </div>
  );
};

export const RecommendEntertainersPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  cityId: string;
  performerType: string;
  badgeId: string;
  recommendationSlug: string;
}> = ({ isVisible, onClose, cityId, performerType, badgeId, recommendationSlug }) => {
  const { entertainerStandouts } = useEntertainersStandoutsQuery({
    cityId: cityId,
    type: performerType,
    badgeId,
    recommendationSlug,
  });

  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    cityId,
    performerType,
  );

  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        {entertainerStandouts && isVisible && (
          <RecommendEntertainersPopupContent
            entertainerStandouts={entertainerStandouts}
            entertainers={entertainers}
            badgeId={badgeId}
            cityId={cityId}
            recommendationSlug={recommendationSlug}
            onClose={onClose}
          />
        )}
      </Popup.Content>
    </Popup>
  );
};

const StandoutSection = ({
  section,
  cityId,
  type: performerType,
}: {
  section: StandoutScreenSection;
  cityId: string;
  type: string;
}) => {
  const { navigate } = useRouter();
  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    cityId,
    performerType,
  );

  if (!section.entertainerIds.length && !section.emptyState) {
    return <></>;
  }

  const sectionEntertainers = section.entertainerIds
    .filter((entertainerId) => entertainers[entertainerId])
    .map((entertainerId) => entertainers[entertainerId]);

  return (
    <div className="mt-4">
      <TitleToolbar
        text={section.title}
        action={
          sectionEntertainers.length || section?.countOverride ? (
            <span>{`${section?.countOverride ?? sectionEntertainers.length} Total`}</span>
          ) : undefined
        }
        size="sm"
      />
      <div>
        {entertainersAreReady ? (
          sectionEntertainers.length ? (
            sectionEntertainers.map((entertainer) => (
              <EntertainerSupportCard
                entertainer={entertainer}
                onClick={() => navigate(`/noauth/entertainer/${entertainer.bookingHandle}`)}
              />
            ))
          ) : (
            <EmptyState title={section.emptyState.title} text={section.emptyState.text} />
          )
        ) : (
          <Skeleton height={120} />
        )}
      </div>
    </div>
  );
};

interface CityEntertainerStandoutsScreenProps {
  cityId: string;
  type: string;
}

interface CityEntertainerStandoutsScreenQuery {
  badgeId?: string;
  recommendationSlug?: string;
}

export const CityEntertainerStandoutsScreen = createScreenComponent<
  CityEntertainerStandoutsScreenProps,
  CityEntertainerStandoutsScreenQuery
>(({ cityId, type: performerType, f7route }) => {
  const { navigate } = useRouter();
  const [isRecommendPopupVisible, setIsRecommendPopupVisible] = useState(false);

  const { cityIsReady, city } = useCityById(cityId);
  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    cityId,
    performerType,
  );

  const { entertainerStandouts } = useEntertainersStandoutsQuery({
    cityId: cityId,
    type: performerType,
    badgeId: f7route.query?.badgeId,
    recommendationSlug: f7route.query?.recommendationSlug,
  });

  return (
    <Screen
      name="Entertainer Standouts Screen"
      headerProps={{
        title: <Screen.GagglTitle />,
      }}
      hideToolbar
      disableLocationPermissionGate
      bottomToolbar={
        <Screen.BottomToolbar maxWidth={425}>
          <Button roundness="rounded" onClick={() => setIsRecommendPopupVisible(true)}>
            Make Recommendation
          </Button>
        </Screen.BottomToolbar>
      }
    >
      <Screen.Content maxWidth={425}>
        {entertainerStandouts && (
          <div>
            <div className="d-flex align-items-center gap-2">
              {entertainerStandouts.imgUrl && (
                <div>
                  <img src={entertainerStandouts.imgUrl} style={{ height: 36, width: 36 }} />
                </div>
              )}
              <div>
                <div
                  style={{
                    fontSize: '.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '.1rem',
                    opacity: 0.7,
                    marginBottom: -4,
                  }}
                >
                  {cityIsReady ? <strong>{city?.name}</strong> : <Skeleton height={16} />}
                </div>
                <div className="fs-4" style={{ lineHeight: 1.2 }}>
                  <strong>{entertainerStandouts.title}</strong>
                </div>
              </div>
            </div>
            {entertainerStandouts.description && (
              <Markdown options={{ forceWrapper: true }}>
                {entertainerStandouts.description}
              </Markdown>
            )}
            {entertainerStandouts.sections.map((section) => (
              <StandoutSection section={section} cityId={cityId} type={performerType} />
            ))}
          </div>
        )}
        {/* <div className="d-flex flex-column gap-2">
          {Object.values(entertainers ?? {}).map((entertainer) => (
            <LazyLoad key={entertainer._id}>
              <EntertainerSupportCard entertainer={entertainer} onClick={setSelectedEntertainer} />
            </LazyLoad>
          ))}
        </div> */}
      </Screen.Content>
      <RecommendEntertainersPopup
        isVisible={isRecommendPopupVisible}
        onClose={() => setIsRecommendPopupVisible(false)}
        cityId={cityId}
        performerType={performerType}
        recommendationSlug={f7route.query?.recommendationSlug}
        badgeId={f7route.query?.badgeId}
      />
    </Screen>
  );
});
